import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

// Create context
const GeldContext = createContext();

// Provider component
export function GeldProvider({ children }) {
  const { user, updateUserData } = useAuth();
  const [geldBalance, setGeldBalance] = useState(0);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize geld balance from user data when component mounts
  useEffect(() => {
    if (user && user.geld_balance !== undefined) {
      setGeldBalance(user.geld_balance || 0);
    }
  }, [user]);

  // Add geld to user's balance
  const addGeld = async (amount, reason = "correct_answer") => {
    // Create transaction ID to track animation and DB update
    const transactionId = Date.now().toString();

    // Add to pending transactions for animation
    setPendingTransactions((prev) => [
      ...prev,
      { id: transactionId, amount, reason, timestamp: Date.now() },
    ]);

    // Update local state immediately for responsive UI
    setGeldBalance((prev) => prev + amount);

    // Update database if user is logged in
    if (user && !isUpdating) {
      setIsUpdating(true);
      try {
        const newBalance = (user.geld_balance || 0) + amount;
        await updateUserData({ geld_balance: newBalance });
      } catch (error) {
        console.error("Failed to update geld balance:", error);
        // Revert the local update if database update fails
        setGeldBalance((prev) => prev - amount);
      } finally {
        setIsUpdating(false);
      }
    }

    // Remove from pending after animation completes
    setTimeout(() => {
      setPendingTransactions((prev) =>
        prev.filter((tx) => tx.id !== transactionId)
      );
    }, 2000);

    return transactionId;
  };

  // Spend geld (for future feature use)
  const spendGeld = async (amount, reason = "purchase") => {
    if (geldBalance < amount) {
      return { success: false, message: "Insufficient geld balance" };
    }

    // Update local state immediately
    setGeldBalance((prev) => prev - amount);

    // Update database if user is logged in
    if (user && !isUpdating) {
      setIsUpdating(true);
      try {
        const newBalance = (user.geld_balance || 0) - amount;
        await updateUserData({ geld_balance: newBalance });
        return { success: true };
      } catch (error) {
        console.error("Failed to update geld balance:", error);
        // Revert the local update if database update fails
        setGeldBalance((prev) => prev + amount);
        return { success: false, message: "Database error" };
      } finally {
        setIsUpdating(false);
      }
    }

    return { success: true };
  };

  // Context value
  const value = {
    geldBalance,
    addGeld,
    spendGeld,
    pendingTransactions,
  };

  return <GeldContext.Provider value={value}>{children}</GeldContext.Provider>;
}

// Custom hook for using geld context
export const useGeld = () => {
  const context = useContext(GeldContext);
  if (context === undefined) {
    throw new Error("useGeld must be used within a GeldProvider");
  }
  return context;
};
